import React, { useMemo, useState } from 'react';
import { CoverageMonthData, TimeGranularity } from './StrategiesUtils';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import { StrategyChart, DatasetConfig } from './StrategyChart';
import { requiredStockLinePlugin } from './RequiredStockLinePlugin';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import { Code, ContentCopy } from '@mui/icons-material';
import { ChartControls } from './ChartControls';
import { onHandColorPlugin } from './onHandColorPlugin';
import { useUser } from '../../../hooks/useUser';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';

interface Props {
  coverageData: CoverageMonthData[];
  matPrice: number;
  unit: IMassUnitObject;
  onTimeChange: (_time: TimeGranularity) => void;
  materialName: string;
  formulaId: string;
  onMonthSelect: (_index: number) => void;
  selectedIndex?: number;
  terminals?: number[] | null;
}

export const CoverageManagement: React.FC<Props> = ({
  coverageData,
  matPrice,
  unit,
  onTimeChange,
  materialName,
  formulaId,
  onMonthSelect,
  selectedIndex,
  terminals,
}) => {
  const { data: user } = useUser();
  const [showCalcDialog, setShowCalcDialog] = useState(false);
  const baseCurrency = useBaseCurrency();
  const { data: formulasPricesData } = useFormulasPrices();

  const terminalCount = useMemo(
    () => (terminals || []).length,
    [terminals],
  );

  // Generate debug calculation text
  const calcText = useMemo(() => {
    if (coverageData.length === 0) return '';
    
    const lines: string[] = [];
    const priceInfo = formulasPricesData?.pricesMap?.[formulaId];
    
    lines.push('COVERAGE CALCULATION BREAKDOWN');
    lines.push('='.repeat(50));
    lines.push('');
    lines.push(`Formula: ${materialName} (${formulaId})`);
    lines.push('');
    
    // Add hedge derivation diagnostics at the top
    lines.push('HEDGE DERIVATION STATUS');
    lines.push('='.repeat(50));
    const totalHedges = coverageData.reduce((sum, m) => {
      const hedgeValue = Number(m.financialPositions) || 0;
      return sum + hedgeValue;
    }, 0);
    const hasAnyHedges = totalHedges > 0;
    
    if (hasAnyHedges) {
      lines.push(`✓ Hedges found: ${totalHedges.toFixed(2)} MT total across all months`);
      const monthsWithHedges = coverageData.filter(m => Number(m.financialPositions) > 0).map(m => m.month);
      lines.push(`  Months with hedges: ${monthsWithHedges.join(', ')}`);
    } else {
      lines.push('⚠ NO HEDGES FOUND for this material');
      lines.push('');
      lines.push('Possible reasons:');
      lines.push('1. No base hedges exist in the database');
      lines.push('2. Hedge derivation not configured for this account');
      lines.push('3. This material is not in the derived materials list');
      lines.push('4. Base material hedges exist but allocation % = 0 for this material');
      lines.push('');
      lines.push('To enable hedge derivation:');
      lines.push('→ Go to "Hedges Deriving" tab in Strategy page');
      lines.push('→ Configure base material and derived materials');
      lines.push('→ Check allocation percentages in the chart');
    }
    lines.push('');
    lines.push('='.repeat(50));
    lines.push('');
    lines.push('PRICING INFORMATION');
    lines.push('='.repeat(50));
    const unitName = unit?.displayName || 'MT';
    if (priceInfo) {
      if (priceInfo.originalCurrency && priceInfo.originalCurrency !== baseCurrency) {
        lines.push(`Original spot price: ${priceInfo.originalPrice?.toLocaleString()} ${priceInfo.originalCurrency}/${unitName}`);
        const fxRate = priceInfo.price / (priceInfo.originalPrice || priceInfo.price);
        lines.push(`FX rate: 1 ${baseCurrency} = ${(1/fxRate).toFixed(4)} ${priceInfo.originalCurrency}`);
        lines.push(`Converted price: ${matPrice.toLocaleString()} ${baseCurrency}/${unitName}`);
      } else {
        lines.push(`Spot price: ${matPrice.toLocaleString()} ${baseCurrency}/${unitName}`);
      }
      if (formulasPricesData.asOfDate) {
        lines.push(`As of: ${new Date(formulasPricesData.asOfDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
      }
    } else {
      lines.push(`Price: ${matPrice.toLocaleString()} ${baseCurrency}/${unitName} (no spot price data)`);
    }
    lines.push('');
    lines.push('='.repeat(50));
    lines.push('');
    lines.push('Coverage Formula:');
    lines.push('Green Bar (Physical) = On-hand + Supply');
    lines.push('Blue Bar (Financial) = Hedges (derived from base material if configured)');
    lines.push('Total Coverage = Physical + Financial');
    lines.push('Grey Bar (What We Need) = Demand + Buffer');
    lines.push('Red Line (Required Coverage) = (Demand + Buffer) x Strategy%');
    lines.push('Coverage % = (Total Coverage / Required Coverage) x 100');
    lines.push('WARNING: Problem if Red Line > Total Coverage (exposure issue)');
    lines.push('');
    lines.push('NOTE: Hedges are automatically derived from base material hedges');
    lines.push('      using allocation percentages calculated from exposure data.');
    lines.push('');
    lines.push('='.repeat(50));
    lines.push('');
    
    // Show on-hand storage
    if (coverageData.length > 0) {
      const onHand = coverageData[0];
      lines.push('Storage On-Hand:');
      lines.push(`  Current Stock: ${onHand.stock.toLocaleString()} MT`);
      lines.push(`  Buffer (protected): ${onHand.emergencyStock.toLocaleString()} MT`);
      lines.push(`  Available for use: ${onHand.inventory.toLocaleString()} MT`);
      lines.push('');
      lines.push('='.repeat(50));
      lines.push('');
    }
    
    // Skip on-hand entry (first item)
    coverageData.slice(1).forEach((month) => {
      const initialStorage = month.stock;
      const supply = month.POs;
      const demand = month.expectedDemand;
      const buffer = month.emergencyStock;
      const hedges = month.financialPositions;
      const currCover = month.currCover;
      const reqCover = month.reqCover;
      const strategyPercent = month.covRatio;
      const covPercentage = month.covPercentage;
      const need = demand + buffer;
      const finalStorage = month.remain;
      
      lines.push(`${month.month}:`);
      lines.push(`  Initial Storage: ${initialStorage.toLocaleString()} MT`);
      lines.push(`  Supply (POs): ${supply.toLocaleString()} MT`);
      lines.push(`  Hedges (Financial): ${hedges.toLocaleString()} MT`);
      lines.push(`  Buffer (protected): ${buffer.toLocaleString()} MT`);
      lines.push(`  Expected Demand: ${demand.toLocaleString()} MT`);
      lines.push(`  Strategy %: ${strategyPercent}%`);
      lines.push('');
      lines.push(`  PHYSICAL (Green Bar):`);
      lines.push(`    ${initialStorage.toLocaleString()} + ${supply.toLocaleString()} = ${(initialStorage + supply).toLocaleString()} MT`);
      lines.push(`  FINANCIAL (Blue Bar):`);
      lines.push(`    ${hedges.toLocaleString()} MT`);
      lines.push(`  TOTAL COVERAGE (Green + Blue):`);
      lines.push(`    ${(initialStorage + supply).toLocaleString()} + ${hedges.toLocaleString()} = ${currCover.toLocaleString()} MT`);
      lines.push(`  GREY BAR (What We Need):`);
      lines.push(`    ${demand.toLocaleString()} + ${buffer.toLocaleString()} = ${need.toLocaleString()} MT`);
      lines.push(`  RED LINE (Required Coverage):`);
      lines.push(`    (${demand.toLocaleString()} + ${buffer.toLocaleString()}) x ${strategyPercent}% = ${reqCover.toLocaleString()} MT`);
      lines.push(`  Coverage %: ${covPercentage}%`);
      if (reqCover > currCover) {
        lines.push(`  STATUS: EXPOSURE - Red (${reqCover.toLocaleString()}) > Blue (${currCover.toLocaleString()})`);
        lines.push(`  ACTION NEEDED: ${(reqCover - currCover).toLocaleString()} MT shortage`);
      } else {
        lines.push(`  STATUS: COVERED - Blue (${currCover.toLocaleString()}) >= Red (${reqCover.toLocaleString()})`);
      }
      lines.push('');
      lines.push(`  STORAGE FLOW:`);
      lines.push(`    Initial: ${initialStorage.toLocaleString()} MT`);
      lines.push(`    + Supply: ${supply.toLocaleString()} MT`);
      lines.push(`    - Demand: ${demand.toLocaleString()} MT`);
      lines.push(`    = Final: ${finalStorage.toLocaleString()} MT (becomes next month initial)`);
      lines.push('');
    });
    
    return lines.join('\n');
  }, [coverageData, materialName, formulaId, matPrice, unit, baseCurrency, formulasPricesData]);

  const datasets: DatasetConfig[] = [
    {
      label: 'Physical coverage',
      dataKey: 'physicalCoverage',
      backgroundColor: '#10B981',
      borderColor: '#059669',
      borderWidth: 0,
      borderRadius: 4,
      type: 'bar',
    },
    {
      label: 'Financial coverage',
      dataKey: 'financialPositions',
      backgroundColor: '#93C5FD',
      borderColor: '#3B82F6',
      borderWidth: 0,
      borderRadius: 4,
      type: 'bar',
    },
    {
      label: 'Expected demand',
      dataKey: 'visExpectDemand',
      borderColor: '#9f9f9f',
      backgroundColor: '#cfcfcf',
      borderRadius: 4,
      type: 'bar',
      fill: true,
      hidden: false,
      residualOf: ['physicalCoverage', 'financialPositions'],
      fullValueKey: 'fullDemand',
    },
    {
      label: 'Expected Consumption (MRP)',
      dataKey: 'requiredQuantity',
      backgroundColor: 'red',
      borderColor: '',
      borderWidth: 0,
      hideFromLegend: false,
      type: 'line',
    },
    {
      label: 'Required coverage',
      dataKey: 'reqCover',
      borderColor: 'red',
      backgroundColor: 'transparent',
      borderWidth: 0,
      type: 'line',
      fill: false,
      hidden: false,
      hideFromLegend: true,
    },
  ];

  const pluginsFactory = useMemo(
    () => (_isQuantity: boolean, _matPrice: number) => {
      return [requiredStockLinePlugin, onHandColorPlugin];
    },
    [],
  );

  return (
    <>
      <StrategyChart
        formulaId={formulaId}
        data={coverageData}
        matPrice={matPrice}
        unit={unit}
        onTimeChange={onTimeChange}
        datasets={datasets}
        pluginsFactory={pluginsFactory}
        stacked={true}
        showPercentageLabels={true}
        extraParams={{ coverage: true }}
        selectedIndex={selectedIndex != null ? selectedIndex + 1 : undefined}
        onBarClick={(index) => {
          // Subtract 1 to account for on-hand bar at index 0
          const adjustedIndex = Math.max(0, index - 1);
          onMonthSelect(adjustedIndex);
        }}
        renderControls={({ isQuantity, setIsQuantity, timeGranularity, setTimeGranularity, viewMonths, setViewMonths }) => (
          <ChartControls
            title={`${materialName} - Coverage ratio management`}
            isQuantity={isQuantity}
            setIsQuantity={setIsQuantity}
            timeGranularity={timeGranularity}
            setTimeGranularity={setTimeGranularity}
            viewMonths={viewMonths}
            setViewMonths={setViewMonths}
            granularityOptions={terminalCount > 0 && terminalCount < 12 ? ['Month', 'Quarter', 'Terminal', 'Yearly', 'Full'] : ['Month', 'Quarter', 'Yearly', 'Full']}
            extraControls={
              user?.isAdmin ? (
                <MuiTooltip title={timeGranularity !== 'Month' ? "Show terminal aggregation calculation" : "Show calculation details"} arrow placement="top">
                  <button
                    onClick={() => setShowCalcDialog(true)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <Code sx={{ fontSize: 17 }} />
                  </button>
                </MuiTooltip>
              ) : undefined
            }
          />
        )}
      />
      <Dialog open={showCalcDialog} onClose={() => setShowCalcDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Coverage Calculation Breakdown</span>
          <IconButton
            size="small"
            onClick={() => {
              try {
                const textToCopy = (window as any).__coverageTerminalDebugLog || calcText;
                if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(textToCopy);
                }
              } catch {
                // ignore clipboard errors
              }
            }}
          >
            <ContentCopy sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <pre className="text-xs font-mono whitespace-pre-wrap bg-gray-50 p-4 rounded">
            {(window as any).__coverageTerminalDebugLog || calcText}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCalcDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
